import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowUpRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { api, serviceByKey } from "../lib/api";
import { format } from "date-fns";

const Row = ({ k, v }) => (
  <div className="flex items-baseline justify-between py-4 border-b hairline">
    <span className="text-sm uppercase tracking-[0.24em] text-muted-foreground">{k}</span>
    <span className="text-foreground text-right max-w-[60%] break-words">{v}</span>
  </div>
);

const Confirmation = () => {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/bookings/${id}`)
      .then(({ data }) => setBooking(data))
      .catch(() => setError("Booking not found"));
  }, [id]);

  return (
    <div className="bg-background text-foreground min-h-screen">
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 lg:px-12 pt-20 pb-32" data-testid="confirmation-section">
        {!booking && !error && (
          <div className="flex items-center justify-center py-32 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-3" />
            Retrieving your request…
          </div>
        )}
        {error && (
          <div className="text-center py-32" data-testid="confirmation-error">
            <p className="font-display text-3xl">{error}</p>
            <Link to="/">
              <Button className="mt-8 rounded-none bg-primary text-primary-foreground">Return home</Button>
            </Link>
          </div>
        )}
        {booking && (
          <div className="fade-up">
            <CheckCircle2 className="h-12 w-12 text-primary" strokeWidth={1.2} />
            <span className="label-eyebrow mt-8 block">— Request received</span>
            <h1 className="font-display mt-6 text-5xl lg:text-6xl leading-[0.98]">
              Thank you, <span className="gold-gradient-text">{booking.full_name.split(" ")[0]}.</span>
            </h1>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-xl">
              Your appointment request is pending review. You will receive a confirmation
              at <span className="text-foreground">{booking.email}</span> once we've reviewed it.
            </p>

            <div className="mt-12 border hairline bg-card p-8">
              <span className="label-eyebrow">Booking summary</span>
              <div className="mt-6">
                <Row k="Reference" v={booking.id.slice(0, 8).toUpperCase()} />
                <Row k="Service" v={serviceByKey(booking.service_type).label} />
                <Row
                  k="Preferred date"
                  v={format(new Date(booking.preferred_date), "EEE, do MMM yyyy")}
                />
                <Row k="Name" v={booking.full_name} />
                <Row k="Email" v={booking.email} />
                <Row k="Phone" v={booking.phone} />
                <Row k="Address" v={booking.address} />
                {booking.notes && <Row k="Notes" v={booking.notes} />}
                <div className="flex items-baseline justify-between py-4">
                  <span className="text-sm uppercase tracking-[0.24em] text-muted-foreground">Status</span>
                  <span className="text-primary uppercase tracking-[0.2em] text-xs" data-testid="booking-status">
                    {booking.status}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/">
                <Button
                  variant="outline"
                  className="rounded-none border-primary/60 text-primary hover:bg-primary/10 px-8 py-6 text-sm uppercase tracking-[0.24em]"
                  data-testid="confirm-home-btn"
                >
                  Return home
                </Button>
              </Link>
              <Link to="/book">
                <Button className="rounded-none bg-primary text-primary-foreground hover:bg-accent px-8 py-6 text-sm uppercase tracking-[0.24em]">
                  Book another visit
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
};

export default Confirmation;
